/**
 * Machine-translation suggestions via Wikimedia's MinT service.
 *
 * MinT is the Wikimedia Foundation's own open-model translation service, the
 * same one Content Translation uses. We call it through cxserver's REST
 * endpoint, which sends `access-control-allow-origin: *` - so this browser-
 * only tool can reach it directly with no server of its own and no API key.
 *
 * Scope and intent: this produces a *suggestion* the contributor edits and
 * approves. Nothing here is automatic and nothing blocks an upload - a
 * failed or slow translation leaves the field exactly as the user left it.
 *
 * Two details the API forces on us:
 *
 *  - The request field is named `html`, and the service really does parse it
 *    as HTML. Sending a raw "<" makes it invent a tag: "5 > 3" came back as
 *    `5&gt; 3` and "<not a tag>" became `<not a="" tag="">`. So text is
 *    HTML-escaped on the way in and unescaped on the way out.
 *  - A cold model load can take ~40 s; a warm one is ~3 s. The timeout is
 *    generous accordingly, and callers show a pending state rather than
 *    blocking the form.
 */

const CXSERVER_MT = "https://cxserver.wikimedia.org/v1/mt"
const PROVIDER = "MinT"

/** Generous: a first request for an unloaded model can take ~40 s. */
const REQUEST_TIMEOUT_MS = 60_000

export type TranslationErrorCode =
  | "same-language"
  | "empty-source"
  | "unsupported-pair"
  | "rate-limited"
  | "http"
  | "network"
  | "timeout"
  | "malformed"

export class TranslationError extends Error {
  code: TranslationErrorCode
  constructor(code: TranslationErrorCode, message: string) {
    super(message)
    this.name = "TranslationError"
    this.code = code
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
}

function unescapeHtml(s: string): string {
  // &amp; is undone last so "&amp;lt;" survives as the literal "&lt;".
  return s
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, "\"")
    .replace(/&#0?39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
}

/**
 * Whether a suggestion is worth offering at all. MinT answers 404 for a
 * same-language pair, so that case is refused here rather than spending a
 * round trip to be told.
 */
export function canRequestTranslation(from: string, to: string): boolean {
  return !!from && !!to && from !== to
}

/**
 * App-wide serialisation for translation requests.
 *
 * The caption editor and the description editor are two independent
 * component instances, so a per-component queue still let them fire at MinT
 * concurrently - which is the exact pattern that returned HTTP 429 in
 * testing. Keeping the queue at module scope means every translation in the
 * app, from either editor, waits its turn.
 */
let queue: Promise<unknown> = Promise.resolve()

export function queueTranslation<T>(task: () => Promise<T>): Promise<T> {
  const run = queue.then(task, task)
  // Swallow rejections on the chain itself so one failure cannot poison
  // every translation queued behind it; callers still see their own error.
  queue = run.then(() => undefined, () => undefined)
  return run
}

export interface TranslateOptions {
  text: string
  from: string
  to: string
  signal?: AbortSignal
}

/**
 * Translate `text` from one language to another, returning the suggestion.
 * Throws TranslationError with a code the caller can turn into a message.
 */
export async function translateText(opts: TranslateOptions): Promise<string> {
  const { text, from, to, signal } = opts

  const source = text.trim()
  if (!source) {
    throw new TranslationError("empty-source", "Nothing to translate.")
  }
  if (!canRequestTranslation(from, to)) {
    throw new TranslationError("same-language", "Source and target are the same language.")
  }

  const url = `${CXSERVER_MT}/${encodeURIComponent(from)}/${encodeURIComponent(to)}/${PROVIDER}`

  const controller = new AbortController()
  let timedOut = false
  const onParentAbort = () => controller.abort()
  signal?.addEventListener("abort", onParentAbort, { once: true })
  const timer = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, REQUEST_TIMEOUT_MS)

  try {
    const resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ html: escapeHtml(source) }),
      signal: controller.signal
    })

    if (resp.status === 404) {
      // cxserver answers 404 both for an unknown language and for a pair
      // MinT has no model for.
      throw new TranslationError(
        "unsupported-pair", `MinT has no model for ${from} to ${to}.`)
    }
    if (resp.status === 429) {
      // Observed in practice when translating into several languages one
      // after another, which is exactly this feature's normal use. Worth
      // its own code so the user is told to wait rather than shown a
      // bare status number.
      throw new TranslationError(
        "rate-limited", "The translation service is rate-limiting this request.")
    }
    if (!resp.ok) {
      throw new TranslationError("http", "Translation service returned HTTP " + resp.status)
    }

    const json = await resp.json() as { contents?: string; error?: { message?: string } }
    if (json.error) {
      throw new TranslationError("http", json.error.message || "Translation service error")
    }
    if (typeof json.contents !== "string") {
      throw new TranslationError("malformed", "Translation service returned no text.")
    }

    return unescapeHtml(json.contents).trim()
  } catch (err) {
    if (err instanceof TranslationError) throw err
    if (timedOut) {
      throw new TranslationError("timeout", "The translation service took too long to answer.")
    }
    if (signal?.aborted) {
      throw new TranslationError("network", "Translation cancelled.")
    }
    throw new TranslationError(
      "network",
      err instanceof Error ? err.message : "Could not reach the translation service.")
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener("abort", onParentAbort)
  }
}
